import { ChartXAxis } from '@toggle/design-system';
import { useElemSize } from '@toggle/helpers';
import * as d3 from 'd3';
import React, { FC, useMemo, useRef } from 'react';

import { IndexedTsPoint } from '~/components/snake-chart/snake-chart-utils';
import { useWindowSize } from '~/hooks/UseWindowSize';

import { ThresholdArea, Variant } from '../threshold-area/ThresholdArea';
import * as S from './ThresholdChart.styles';

export interface ThresholdChartProps {
  tsData: IndexedTsPoint[];
  upperBound: number;
  lowerBound: number;
  locked?: boolean;
  midValue: number;
  thresholdVariants: [Variant, Variant];
}

const RIGHT_PADDING = 65;
const X_AXIS_TOP_PADDING = 16;
const MARGIN_BOTTOM = 37;

export const ThresholdChart: FC<ThresholdChartProps> = ({
  tsData,
  upperBound,
  lowerBound,
  thresholdVariants,
  midValue,
}) => {
  useWindowSize();
  const svgRef = useRef<SVGSVGElement>(null);
  const { width, height } = useElemSize(svgRef);
  const DOMAIN_Y_PADDING = 0.1;
  const FALLBACK_DOMAIN_PADDING = 0.000001;

  const [minY = 0, maxY = 0] = useMemo(
    () => d3.extent(tsData.map(p => p.value)),
    [tsData]
  );

  const xScale = useMemo(() => {
    return d3
      .scaleLinear()
      .domain([0, tsData.length])
      .range([0, width - RIGHT_PADDING]);
  }, [tsData, width]);

  const yValPadding = Math.max(
    (maxY - minY) * DOMAIN_Y_PADDING,
    FALLBACK_DOMAIN_PADDING
  );

  const yScale = useMemo(() => {
    return d3
      .scaleLinear()
      .domain([minY, maxY])
      .range([Math.max(0, height - MARGIN_BOTTOM), 0]);
  }, [height, minY, maxY]);

  const paddedYScale = useMemo(() => {
    return d3
      .scaleLinear()
      .domain([minY - yValPadding, maxY + yValPadding])
      .range([Math.max(0, height - MARGIN_BOTTOM), 0]);
  }, [height, minY, maxY, yValPadding]);

  const plotLine = useMemo(
    () =>
      d3
        .line<typeof tsData[0]>()
        .x(p => xScale(p.index))
        .y(p => paddedYScale(p.value))
        .curve(d3.curveLinear)(tsData) ?? '',
    [xScale, tsData, paddedYScale]
  );

  const minValue = d3.min(tsData, d => d.value);

  if (minValue === undefined) {
    return null;
  }

  const xAxisOffset = paddedYScale(midValue);
  const toUpperBound = paddedYScale(upperBound);
  const toLowerBound = paddedYScale(lowerBound);
  const bullishAreaHeight = toUpperBound;
  const bearishAreaHeight = Math.max(0, paddedYScale(minValue) - toLowerBound);
  const adjustedWidth = width - RIGHT_PADDING;
  const textAlignOffset = adjustedWidth + 10;

  return (
    <section>
      <S.SvgChart data-testid="threshold-chart" ref={svgRef}>
        <S.RectBackground width="100%" height="100%" />
        {!!tsData.length && (
          <ChartXAxis
            tsData={tsData}
            xScale={xScale}
            yScale={yScale}
            padding={-X_AXIS_TOP_PADDING}
            domainLimited
          />
        )}
        <S.LineXAxis
          data-testid="zero-line"
          x1="0"
          y1={xAxisOffset}
          x2={adjustedWidth}
          y2={xAxisOffset}
        />
        <S.PathPlot d={plotLine} />

        <S.ThresholdAxis transform={`translate(0, ${xAxisOffset})`}>
          <S.AxisText
            textAnchor="start"
            x={textAlignOffset}
            alignmentBaseline="middle"
          >
            {midValue}
          </S.AxisText>
        </S.ThresholdAxis>

        <g transform={`translate(0, ${toUpperBound})`}>
          <S.AxisText
            textAnchor="start"
            x={textAlignOffset}
            alignmentBaseline="middle"
          >
            {upperBound}
          </S.AxisText>
        </g>

        <ThresholdArea
          height={bullishAreaHeight}
          edgeOffset={toUpperBound}
          variant={thresholdVariants[0]}
          width={adjustedWidth}
        />
        <g transform={`translate(0, ${toLowerBound})`}>
          <S.AxisText
            textAnchor="start"
            x={textAlignOffset}
            alignmentBaseline="middle"
          >
            {lowerBound}
          </S.AxisText>
        </g>

        <ThresholdArea
          height={bearishAreaHeight}
          offset={toLowerBound}
          variant={thresholdVariants[1]}
          width={adjustedWidth}
        />
      </S.SvgChart>
    </section>
  );
};
